import { Injectable, inject } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { AddressFirestoreService } from '../../services/address.firestore.service';
import { AddressActions } from './address.action';
import { OrmService } from '../../schema/orm.service';
import { AddressStateModel } from './address.store';
import { patchState } from '../../nested-value-getter';

@State<AddressStateModel>({
  name: 'addresses',
  defaults: {
    addresses: [],
    selectedUser: undefined,
    isSubscribed: false,
    isLoading: false,
    filter: undefined,
  },
})
@Injectable()
export class AddressState {
  private dbUser = inject(OrmService).userCollection;
  private dbUserGroup = inject(OrmService).groupsOfUsers;
  private addressService = inject(AddressFirestoreService);

  @Action(AddressActions.GetAllAddress)
  async getAll(ctx: StateContext<AddressStateModel>) {
    ctx.patchState({ isLoading: true });
    const address = await this.addressService.GetAll();
    ctx.patchState({
      addresses: address.map(({ data, ref }) => ({ ...data, ref })),
      isLoading: false,
    });
  }

  @Action(AddressActions.CreateAddress)
  async create(ctx: StateContext<AddressStateModel>, { userRef, addressData }: AddressActions.CreateAddress) {
    await this.addressService.Create(userRef, addressData);
  }

  @Action(AddressActions.QueryAddress)
  async query(ctx: StateContext<AddressStateModel>, { filter }: AddressActions.QueryAddress) {
    ctx.patchState({ isLoading: true });
    const addresses = await this.addressService.QueryAll(filter.query);
    ctx.patchState({
      addresses: addresses.map(({ data, ref }) => ({ ...data, ref })),
      isLoading: false,
    });
  }

  @Action(AddressActions.InitAddress)
  initAddresses(ctx: StateContext<AddressStateModel>, { id }: AddressActions.InitAddress) {
    const state = ctx.getState();
    if (!state.isSubscribed) {
      ctx.patchState({ isSubscribed: true });
      let off;
      if (id) {
        off = this.dbUser(this.dbUser.id(id))
          .addresses.all()
          .on((addresses) => {
            ctx.patchState({ isLoading: true });
            ctx.patchState({ addresses: addresses.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
          });
      } else {
        off = this.addressService.OnGetAll((data) => ctx.dispatch(new AddressActions.UpdateAddressResult(data)));
      }
      off.catch((err) => {
        (err as Error).message = `InitAddressesCatch: ${(err as Error).message}`;
        return ctx.dispatch(new AddressActions.AddressError(err as Error));
      });
      ctx.patchState({ off });
    }
  }

  @Action(AddressActions.CloseAddressConnection)
  closeMachinesConnection(ctx: StateContext<AddressStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed && state.off) {
      state.off();
    }
    ctx.patchState({ isSubscribed: false });
  }

  @Action(AddressActions.UpdateAddress)
  async updateMachine(ctx: StateContext<AddressStateModel>, { addressData, id }: AddressActions.UpdateAddress) {
    await this.addressService.Update(id, addressData);
  }

  @Action(AddressActions.UpdateAddressResult)
  updateAddressResult(ctx: StateContext<AddressStateModel>, { data }: AddressActions.UpdateAddressResult) {
    patchState<AddressStateModel>(ctx, 'addresses', data);
  }

  @Action(AddressActions.AddressError)
  machineError(ctx: StateContext<AddressStateModel>, { error }: AddressActions.AddressError) {
    throw error;
  }

  @Action(AddressActions.SetSelectedUser)
  setSelectedUser(ctx: StateContext<AddressStateModel>, { selectedUser }: AddressActions.SetSelectedUser) {
    ctx.patchState({ selectedUser });
  }

  @Selector()
  static addresses(state: AddressStateModel) {
    return state.addresses;
  }

  @Selector()
  static addressesByUser(state: AddressStateModel) {
    const userRef: string | undefined = state.selectedUser?.ref?.id;
    if (userRef) {
      return state.addresses.filter((address) => address.ref.collection.path.search(userRef) !== -1);
    }
    return undefined;
  }

  @Selector()
  static addressState(state: AddressStateModel) {
    return state;
  }
}
